import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, CheckCircle2, Mail, Linkedin, Twitter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 mb-4" data-testid="contact-badge">
            Get In Touch
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="contact-title">
            Let's Build Something Amazing
          </h2>
          <p className="text-xl text-slate-300" data-testid="contact-description">
            Have a project in mind? We'd love to hear about it. Reach out and let's discuss how we can bring your vision to life.
          </p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700" data-testid="contact-form-card">
          <CardContent className="p-8">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-600/20 border border-green-600/30 rounded-lg flex items-center text-green-400" data-testid="contact-success-message">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Thank you! We'll get back to you soon.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-600/20 border border-red-600/30 rounded-lg text-red-400" data-testid="contact-error-message">
                Something went wrong. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2" data-testid="contact-label-name">
                  Name
                </label>
                <Input 
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="Your name"
                  required
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2" data-testid="contact-label-email">
                  Email
                </label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="your@email.com"
                  required
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2" data-testid="contact-label-message">
                  Message
                </label>
                <Textarea 
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white min-h-[150px]"
                  placeholder="Tell us about your project..."
                  required
                  data-testid="contact-input-message"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </form>

            <div className="mt-8 pt-8 border-t border-slate-700">
              <div className="flex justify-center space-x-6">
                <a href="mailto:contact@nexuscreativegroupllc.com" className="text-slate-400 hover:text-white transition-colors" data-testid="contact-email-link">
                  <Mail className="w-6 h-6" />
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="contact-linkedin-link">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="contact-twitter-link">
                  <Twitter className="w-6 h-6" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactSection;